import ProfileModel from './profile-model';
import ProfileView from './profile-view';

export default class ProfilePresenter {
  constructor(model, view, appInterface) {
    this.model = model || new ProfileModel();
    this.view = view || new ProfileView();
    this.appInterface = appInterface || this.createDefaultAppInterface();
  }

  createDefaultAppInterface() {
    return {
      showToast: (message, type, duration) => {
        if (window.app && window.app.showToast) {
          window.app.showToast(message, type, duration);
        }
      },
      showConfirmDialog: (message, onConfirm) => {
        if (window.app && window.app.showConfirmDialog) {
          window.app.showConfirmDialog(message, onConfirm);
        } else {
          if (confirm(message)) {
            onConfirm();
          }
        }
      },
      navigateTo: (page) => {
        if (window.app && window.app.navigateTo) {
          window.app.navigateTo(page);
        }
      },
      setAuthState: (isAuthenticated, user) => {
        if (window.app && window.app.setAuthState) {
          window.app.setAuthState(isAuthenticated, user);
        }
      },
      enableNotifications: () => {
        if (window.app && window.app.enableNotifications) {
          window.app.enableNotifications();
        }
      },
      unsubscribeFromNotifications: () => {
        if (window.app && window.app.unsubscribeFromNotifications) {
          window.app.unsubscribeFromNotifications();
        }
      },
      setTheme: (theme) => {
        if (window.app && window.app.setTheme) {
          window.app.setTheme(theme);
        }
      },
      setFont: (font) => {
        if (window.app && window.app.setFont) {
          window.app.setFont(font);
        }
      },
      updateMapLayers: () => {
        if (window.app && window.app.updateMapLayers) {
          window.app.updateMapLayers();
        }
      },
      reinitTooltips: () => {
        if (window.app && window.app.tooltip && window.app.tooltip.reinit) {
          window.app.tooltip.reinit();
        }
      }
    };
  }

  async init() {
    this.model.loadUserState();
    this.setupEventListeners();
    this.switchTab('account');
    this.updateUISettings();
  }

  setupEventListeners() {
    this.view.setupTabEventListeners((tabName) => this.switchTab(tabName));
    this.view.setupThemeEventListeners((theme) => this.changeTheme(theme));
    this.view.setupFontEventListeners((font) => this.changeFont(font));
    this.view.setupLogoutEventListener(() => this.logout());
    this.view.setupNotificationEventListener(() => this.toggleNotifications());
    this.view.setupLoginButtonEventListener(() => this.navigateToAuth());
  }

  switchTab(tabName) {
    this.view.switchTab(tabName);
    
    if (tabName === 'account') {
      this.renderAccountPanel();
    }
    
    this.appInterface.reinitTooltips();
  }

  renderAccountPanel() {
    const userData = this.model.getUserData();
    this.view.renderAccountPanel(userData);
  }

  changeTheme(theme) {
    const success = this.model.updateTheme(theme);
    
    if (success) {
      this.view.updateThemeSettings(theme);
      if (window.app && window.app.setTheme) {
        window.app.setTheme(theme);
      }
      this.appInterface.showToast(`Tema berubah ke ${theme === 'dark' ? 'gelap' : 'terang'}`, 'success');
    }
  }
  
  changeFont(font) {
    const success = this.model.updateFont(font);
    
    if (success) {
      this.view.updateFontSettings(font);
      if (window.app && window.app.setFont) {
        window.app.setFont(font);
      }
      
      const fontName = font === 'disleksia' ? 'ramah disleksia' : 'bawaan';
      this.appInterface.showToast(`Font berubah ke ${fontName}`, 'success');
    }
  }

  toggleNotifications() {
    const isSubscribed = this.model.isPushSubscribed();
    
    if (isSubscribed) {
      this.appInterface.unsubscribeFromNotifications();
    } else {
      this.appInterface.enableNotifications();
    }
  }

  logout() {
    this.appInterface.showConfirmDialog(
      'Yakin ingin keluar?',
      () => {
        const success = this.model.logout();
        
        if (success) {
          if (window.app) {
            window.app.isAuthenticated = false;
            window.app.isGuest = true;
            window.app.user = {};
            window.app.token = null;
          }
          
          this.appInterface.navigateTo('home');
          this.appInterface.showToast('Berhasil keluar', 'success');
        }
      }
    );
  }

  navigateToAuth() {
    this.appInterface.navigateTo('auth');
  }

  updateUISettings() {
    const currentTheme = this.model.getCurrentTheme();
    const currentFont = this.model.getCurrentFont();
    const isSubscribed = this.model.isPushSubscribed();
    
    this.view.updateThemeSettings(currentTheme);
    this.view.updateFontSettings(currentFont);
    this.view.updateNotificationButton(isSubscribed);
  }

  updateNotificationButton(isSubscribed) {
    this.model.setPushSubscription(isSubscribed ? {} : null);
    this.view.updateNotificationButton(isSubscribed);
  }

  refresh() {
    this.model.loadUserState();
    this.renderAccountPanel();
    this.updateUISettings();
  }

  cleanup() {
    this.view.cleanup();
  }
}